import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { PageHero, Section } from "@/components/PageParts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ExternalLink, CheckCircle2 } from "lucide-react";

const SERVICES = ["Assisted Living", "Family Care Homes", "Memory Care", "Independent Living",
  "Private-Duty Home Care", "Personal Care", "Companion Care", "Residential Respite", "In-Home Respite",
  "Adult Day Services", "Care Management", "Senior Transition / Move Management", "Private Transportation"];

export default function Join() {
  const settings = useSettings();
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    name: "", email: "", password: "", phone: "", city: "", state: "",
    professional_background: "", healthcare_experience: "", sales_experience: "", target_markets: "",
    availability: "", reason_for_joining: "", w9_status: "not_started", company_website: "",
  });
  const [services, setServices] = useState([]);
  const [acks, setAcks] = useState({ agree_compliance: false, ack_income_not_guaranteed: false, ack_not_employee: false, ack_compensation_written: false });

  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });
  const toggleService = (s) => setServices((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);

  const submit = async (e) => {
    e.preventDefault();
    if (!Object.values(acks).every(Boolean)) { toast.error("Please accept all acknowledgments and the compliance agreement."); return; }
    setLoading(true);
    try {
      await api.post("/agent-applications", { ...f, service_interests: services, ...acks });
      if (!user && f.password) {
        try { await register({ name: f.name, email: f.email, password: f.password, role: "PLACEMENT_PROFESSIONAL" }); } catch (_) {}
      }
      setSubmitted(true);
      toast.success("Application submitted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <Section className="py-24">
        <Card className="mx-auto max-w-xl p-10 text-center" data-testid="join-success">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emeraldbrand" />
          <h2 className="mt-4 font-heading text-2xl font-semibold text-navy">Application received</h2>
          <p className="mt-3 text-slate-500">
            Thank you for applying. Applications are reviewed manually — we do not automatically approve agents. Next,
            join the Skool community to activate your {settings.membership_price} membership.
          </p>
          <div className="mt-7 flex flex-col gap-3">
            <Button data-testid="join-skool-btn" size="lg" onClick={() => window.open(settings.skool_membership_url, "_blank")}
              className="bg-clinical hover:bg-clinical/90">
              Join Skool — {settings.membership_price} <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => nav("/login")}>Go to login</Button>
          </div>
        </Card>
      </Section>
    );
  }

  return (
    <div>
      <PageHero eyebrow="Join Care Placement Pro" title={`Membership — ${settings.membership_price}`}
        subtitle="Apply to become a placement professional and join the Skool community. Complete the application below; you can also create your dashboard login." />
      <Section className="py-16">
        <div className="grid gap-8 lg:grid-cols-3">
          <form onSubmit={submit} className="space-y-6 lg:col-span-2" data-testid="agent-application-form">
            <input type="text" name="company_website" tabIndex={-1} autoComplete="off" aria-hidden="true"
              value={f.company_website} onChange={set("company_website")}
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
            <Card className="p-6">
              <h3 className="font-heading text-lg font-semibold text-navy">Your details</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field label="Full name" required><Input data-testid="apply-name" required value={f.name} onChange={set("name")} /></Field>
                <Field label="Email" required><Input data-testid="apply-email" type="email" required value={f.email} onChange={set("email")} /></Field>
                <Field label="Create password (for dashboard)"><Input data-testid="apply-password" type="password" value={f.password} onChange={set("password")} placeholder="Optional — enables login" /></Field>
                <Field label="Phone" required><Input data-testid="apply-phone" required value={f.phone} onChange={set("phone")} /></Field>
                <Field label="City" required><Input data-testid="apply-city" required value={f.city} onChange={set("city")} /></Field>
                <Field label="State" required><Input data-testid="apply-state" required value={f.state} onChange={set("state")} /></Field>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-heading text-lg font-semibold text-navy">Background</h3>
              <div className="mt-4 grid gap-4">
                <Field label="Professional background"><Textarea data-testid="apply-background" value={f.professional_background} onChange={set("professional_background")} /></Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Healthcare experience"><Textarea data-testid="apply-healthcare" value={f.healthcare_experience} onChange={set("healthcare_experience")} /></Field>
                  <Field label="Sales / business-development experience"><Textarea data-testid="apply-sales" value={f.sales_experience} onChange={set("sales_experience")} /></Field>
                </div>
                <Field label="Target markets"><Input data-testid="apply-markets" value={f.target_markets} onChange={set("target_markets")} placeholder="e.g. Wake County, NC" /></Field>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Service interests</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {SERVICES.map((s) => (
                      <button type="button" key={s} onClick={() => toggleService(s)} data-testid={`apply-service-${s}`}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                          services.includes(s) ? "border-clinical bg-clinical text-white" : "border-slate-300 text-slate-600 hover:border-clinical"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Availability"><Input data-testid="apply-availability" value={f.availability} onChange={set("availability")} placeholder="e.g. Part-time / full-time" /></Field>
                  <Field label="W-9 completion status">
                    <Select value={f.w9_status} onValueChange={(v) => setF({ ...f, w9_status: v })}>
                      <SelectTrigger data-testid="apply-w9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_started">Not started</SelectItem>
                        <SelectItem value="in_progress">In progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Why do you want to join?"><Textarea data-testid="apply-reason" value={f.reason_for_joining} onChange={set("reason_for_joining")} /></Field>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-heading text-lg font-semibold text-navy">Acknowledgments</h3>
              <div className="mt-4 space-y-3">
                {[["agree_compliance", "I agree to follow all Care Placement Pro compliance policies."],
                  ["ack_income_not_guaranteed", "I understand income is not guaranteed."],
                  ["ack_not_employee", "I understand I am not automatically an employee."],
                  ["ack_compensation_written", "I understand compensation depends on approved written arrangements."]].map(([k, label]) => (
                  <label key={k} className="flex cursor-pointer items-start gap-3 text-sm text-slate-600">
                    <Checkbox data-testid={`apply-${k}`} checked={acks[k]} onCheckedChange={(v) => setAcks({ ...acks, [k]: !!v })} className="mt-0.5" />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </Card>

            <Button type="submit" size="lg" disabled={loading} data-testid="apply-submit-btn" className="w-full bg-navy hover:bg-navy/90">
              {loading ? "Submitting…" : "Submit application"}
            </Button>
          </form>

          <div className="lg:col-span-1">
            <Card className="sticky top-24 bg-navy p-6 text-white">
              <p className="text-xs font-semibold uppercase tracking-wide text-clinical">Membership</p>
              <p className="mt-2 font-heading text-3xl font-bold">{settings.membership_price}</p>
              <p className="mt-3 text-sm text-slate-300">Hosted and paid through the Skool community.</p>
              <Button data-testid="join-side-skool-btn" onClick={() => window.open(settings.skool_membership_url, "_blank")}
                className="mt-5 w-full bg-clinical hover:bg-clinical/90">
                Join Skool now <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
              <div className="mt-6 border-t border-white/10 pt-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-clinical">Accelerator</p>
                <p className="mt-2 font-heading text-2xl font-bold">{settings.accelerator_price}</p>
                <Button variant="outline" data-testid="join-side-accelerator-btn" onClick={() => nav("/business-in-a-box")}
                  className="mt-3 w-full border-white/30 bg-transparent text-white hover:bg-white/10">
                  Business-in-a-Box
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </Section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}{required && " *"}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
